import React, { useMemo, useState } from 'react'
import {
  Button,
  CenteredSpinner,
  Modal,
  SummaryOfBenefitsCoverage,
  useToaster
} from 'react-angle-dashboard-components'
import { useQuery } from 'react-query'
import { Link, useHistory } from 'react-router-dom'

import { CostInformation, PlanWrapper } from '../../components/PlanInformation'
import useStep from '../../hooks/use-step'
import { GbsStep } from '../../store/interfaces'
import { useGbsContext } from '../../store'
import { YourPlanDetails } from './YourPlanDetails'
import { MainCostsSection } from './MainCostsSection'
import { planService } from '../../services'
import { ISelectablePlan } from '../../interfaces/plan'
import { Paths } from '../../routes/paths'
import {
  PDFSectionHeader,
  PDFSectionText,
  PDFSectionWrapper,
  Header,
  HeaderWrapper,
  EditPlanLink
} from './styles'
import { capitalize } from '@angle/utils'

const stepKey = 'finalQuoteDetails'

const FinalQuoteDetails = () => {
  const history = useHistory()
  const { dispatch } = useGbsContext()
  const [show, setShow] = useState(false)
  const [currentSBCData, setSBCData] = useState<any>(null)
  const [sbcIsLoading, setSbcLoading] = useState(false)
  const toaster = useToaster()

  const stepConfig: Omit<GbsStep, 'data'> = useMemo(() => {
    return {
      bottomBar: {
        hidden: true
      },
      topBar: {
        hidden: true
      }
    }
  }, [])

  useStep(stepKey, stepConfig, dispatch)

  const { data: plans, isLoading } = useQuery('selectedPlans', () =>
    planService.getSelectablePlans({ selected: 1 })
  )

  const handleShowPlanDetails = async (id: string, plan: ISelectablePlan) => {
    try {
      setSbcLoading(true)
      setShow(true)
      const { events, faqs } = await planService.getSBC(id)

      setSBCData({
        benefits: events,
        faqs,
        plan,
        coverageInfo: {
          plan: {
            name: plan.name,
            metalTier: plan?.tier,
            type: plan?.type
          },
          coveragePeriod: {
            from: plan?.startDate,
            to: plan?.endDate
          },
          coverageFor: plan?.eligibleMember?.map(capitalize).join('/') //'Single/Family'
        }
      })
      setSbcLoading(false)
    } catch (e) {
      setSbcLoading(false)
      setShow(false)
      toaster.show({
        message: "We can't get the SBC information",
        icon: 'icon-cancel',
        type: 'danger'
      })
    }
  }

  // get cheapest plan total employer premium to determine 'starting at' cost
  const cheapestPlanTotal = plans?.length
    ? plans[plans.length - 1].totalEmployerCost
    : 0

  return (
    <>
      <div className="bg-light">
        <HeaderWrapper>
          <Header>Final Costs &amp; Benefits</Header>
          <span className="text-white fs-16">Congratulations!</span>
        </HeaderWrapper>
        <MainCostsSection
          totalEmployerCost={cheapestPlanTotal}
          isLoading={isLoading}
        />
        <PDFSectionWrapper>
          <div>
            <PDFSectionHeader>Need more time?</PDFSectionHeader>
            <PDFSectionText>
              Download a print ready PDF to review with your team
            </PDFSectionText>
          </div>
          <Button
            variant="outline-primary"
            className="py-1 bg-white fs-16 mx-3"
          >
            <Link to="/download" target="_blank" rel="noopener noreferrer">
              Download
            </Link>
          </Button>
        </PDFSectionWrapper>
        <YourPlanDetails />
        <div className="d-flex justify-content-center flex-wrap pt-5">
          {plans?.map((plan) => (
            <PlanWrapper key={plan.id} style={{ height: 525, marginRight: 30 }}>
              <CostInformation
                plan={plan}
                compressedVersion
                onShowPlanDetails={handleShowPlanDetails}
              />
            </PlanWrapper>
          ))}
        </div>
        <EditPlanLink
          variant="link"
          className="text-dark"
          onClick={() => history.push(Paths.yourPlans)}
        >
          Edit Plan Selection
        </EditPlanLink>
      </div>
      <Modal
        show={show}
        onHide={() => setShow(false)}
        dialogClassName="w-100 m-0 mw-100"
        className="px-0"
        aria-labelledby="example-custom-modal-styling-title"
        style={{ zIndex: 9999 }}
      >
        <Modal.Header closeButton></Modal.Header>
        <Modal.Body style={{ minHeight: '95vh' }}>
          {sbcIsLoading && <CenteredSpinner />}
          {currentSBCData && !sbcIsLoading && (
            <SummaryOfBenefitsCoverage
              coverageInfo={currentSBCData.coverageInfo}
              benefits={currentSBCData.benefits}
              faqs={currentSBCData.faqs}
            />
          )}
        </Modal.Body>
      </Modal>
    </>
  )
}

export default FinalQuoteDetails
